/**
 * BroadcastChannel-based coordinator for multiple browser tabs viewing the same activity.
 *
 * Purpose: when one tab is actively streaming (SSE connected), other tabs should NOT
 * open a competing SSE connection. Instead, they poll for state and let the active tab
 * drive the session. This prevents `abortPreviousSession()` from killing a healthy stream.
 *
 * Protocol:
 *   - Each tab announces `claim_stream` when it starts an SSE connection.
 *   - Each tab announces `release_stream` when its SSE connection ends.
 *   - Other tabs listen for `claim_stream` and avoid opening their own SSE.
 *   - On `release_stream`, any waiting tab may claim.
 *   - `heartbeat` is sent periodically (10s) so other tabs can detect stale claims.
 */

export type TabCoordinatorMessage =
  | {
      type: 'claim_stream';
      activityId: string;
      tabId: string;
      timestamp: number;
    }
  | { type: 'release_stream'; activityId: string; tabId: string }
  | { type: 'heartbeat'; activityId: string; tabId: string; timestamp: number };

const CHANNEL_NAME = 'spassu-code-pilot-tab';
const HEARTBEAT_INTERVAL_MS = 10_000;
const STALE_THRESHOLD_MS = 25_000;

/** Generates a unique ID per coordinator instance. */
function generateTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface PilotTabCoordinator {
  /** Attempt to claim the stream for this activity. Returns true if claim succeeded. */
  claimStream(activityId: string): boolean;
  /** Claim the stream even if another tab still has a stale local claim. */
  forceClaimStream(activityId: string): void;
  /** Release the stream claim. */
  releaseStream(activityId: string): void;
  /** Check if another tab currently holds the stream for this activity. */
  isStreamClaimedByOtherTab(activityId: string): boolean;
  /** Register a callback for when another tab claims the stream (this tab should stop). */
  onStreamStolen(callback: (activityId: string) => void): void;
  /** Register a callback for when the stream is released (this tab may claim). */
  onStreamReleased(callback: (activityId: string) => void): void;
  /** Clean up resources. */
  destroy(): void;
}

export function createPilotTabCoordinator(): PilotTabCoordinator {
  const tabId = generateTabId();

  // Track which activity this tab has claimed
  let claimedActivityId: string | null = null;

  // Track claims by other tabs: activityId -> { tabId, timestamp }
  const otherTabClaims = new Map<
    string,
    { tabId: string; timestamp: number }
  >();

  // Callbacks
  const stolenCallbacks: Array<(activityId: string) => void> = [];
  const releasedCallbacks: Array<(activityId: string) => void> = [];

  // BroadcastChannel (may not exist in SSR or old browsers)
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    // BroadcastChannel not available — single-tab fallback (all claims succeed)
  }

  const send = (msg: TabCoordinatorMessage) => {
    try {
      channel?.postMessage(msg);
    } catch {
      // Channel closed or errored — ignore
    }
  };

  // Heartbeat interval
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatInterval = setInterval(() => {
      if (claimedActivityId) {
        send({
          type: 'heartbeat',
          activityId: claimedActivityId,
          tabId,
          timestamp: Date.now(),
        });
      }
    }, HEARTBEAT_INTERVAL_MS);
  };

  const adoptClaim = (activityId: string) => {
    otherTabClaims.delete(activityId);
    claimedActivityId = activityId;
    send({ type: 'claim_stream', activityId, tabId, timestamp: Date.now() });
    startHeartbeat();
  };

  // Listen for messages from other tabs
  if (channel) {
    channel.onmessage = (event: MessageEvent<TabCoordinatorMessage>) => {
      const msg = event.data;
      if (!msg || !msg.type) return;

      // Ignore own messages
      if (msg.tabId === tabId) return;

      switch (msg.type) {
        case 'claim_stream':
          otherTabClaims.set(msg.activityId, {
            tabId: msg.tabId,
            timestamp: msg.timestamp,
          });
          // If we had claimed the same activity, we lost it
          if (claimedActivityId === msg.activityId) {
            claimedActivityId = null;
            stopHeartbeat();
            stolenCallbacks.forEach(cb => cb(msg.activityId));
          }
          break;

        case 'release_stream':
          otherTabClaims.delete(msg.activityId);
          releasedCallbacks.forEach(cb => cb(msg.activityId));
          break;

        case 'heartbeat':
          otherTabClaims.set(msg.activityId, {
            tabId: msg.tabId,
            timestamp: msg.timestamp,
          });
          break;
        default:
          break;
      }
    };
  }

  return {
    claimStream(activityId: string): boolean {
      // Check if another tab has a fresh claim
      const existing = otherTabClaims.get(activityId);
      if (existing && Date.now() - existing.timestamp < STALE_THRESHOLD_MS) {
        return false;
      }

      // Stale or no claim — take it
      adoptClaim(activityId);
      return true;
    },

    forceClaimStream(activityId: string): void {
      adoptClaim(activityId);
    },

    releaseStream(activityId: string): void {
      if (claimedActivityId === activityId) {
        claimedActivityId = null;
        stopHeartbeat();
        send({ type: 'release_stream', activityId, tabId });
      }
    },

    isStreamClaimedByOtherTab(activityId: string): boolean {
      const existing = otherTabClaims.get(activityId);
      if (!existing) return false;
      if (Date.now() - existing.timestamp > STALE_THRESHOLD_MS) {
        otherTabClaims.delete(activityId);
        return false;
      }
      return true;
    },

    onStreamStolen(callback: (activityId: string) => void): void {
      stolenCallbacks.push(callback);
    },

    onStreamReleased(callback: (activityId: string) => void): void {
      releasedCallbacks.push(callback);
    },

    destroy(): void {
      stopHeartbeat();
      if (claimedActivityId) {
        send({ type: 'release_stream', activityId: claimedActivityId, tabId });
        claimedActivityId = null;
      }
      try {
        channel?.close();
      } catch {
        // Already closed
      }
      channel = null;
      otherTabClaims.clear();
      stolenCallbacks.length = 0;
      releasedCallbacks.length = 0;
    },
  };
}
